import { APPLICATION_STATUSES } from "@cv-screener/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { RefreshCw, RotateCcw, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ApplicationsTable } from "../components/applications-table";
import { DependencyNotice, Dialog, ErrorState, Loading, MultiSelect, pageSizeOptions, PageHeader, SearchField, Toast } from "../components/ui";
import { api } from "../lib/api";
import type { ApplicationSort, ApplicationSortDirection, ApplicationSortKey } from "../lib/application-table";

const scoreRanges = {
  "85-100": { label: "85–100", min: 85, max: 101 },
  "70-84": { label: "70–84.9", min: 70, max: 85 },
  "50-69": { label: "50–69.9", min: 50, max: 70 },
  "0-49": { label: "0–49.9", min: 0, max: 50 },
  all: { label: "All scores", min: 0, max: 101 },
} as const;

type ScoreRange = keyof typeof scoreRanges;
const applicationSortKeys: ApplicationSortKey[] = ["candidate", "job", "score", "confidence", "status"];

function applicationSortsFromParams(params: URLSearchParams): ApplicationSort[] | undefined {
  const sorts = params.getAll("sort").flatMap((value) => {
    const [key, direction] = value.split(":");
    return applicationSortKeys.includes(key as ApplicationSortKey) && (direction === "ascending" || direction === "descending")
      ? [{ key: key as ApplicationSortKey, direction: direction as ApplicationSortDirection }]
      : [];
  });
  return sorts.length ? sorts : undefined;
}

export function ApplicationsPage() {
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [toast, setToast] = useState<string>();
  const search = params.get("q") ?? "";
  const requestedPage = Number(params.get("page") ?? "1");
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const requestedPageSize = Number(params.get("pageSize") ?? pageSizeOptions[0]);
  const pageSize = pageSizeOptions.includes(requestedPageSize as typeof pageSizeOptions[number]) ? requestedPageSize : pageSizeOptions[0];
  const applicationSorts = applicationSortsFromParams(params);
  const selectedScoreRanges = params.getAll("score").filter((value): value is ScoreRange => value in scoreRanges);
  const scoreRangesSelected: ScoreRange[] = selectedScoreRanges.length ? selectedScoreRanges : ["all"];
  const selectedJobs = params.getAll("job");
  const jobsSelected = selectedJobs.length ? selectedJobs : ["all"];
  const selectedStatuses = params.getAll("status");
  const statusesSelected = selectedStatuses.length ? selectedStatuses : ["all"];
  const { data, isLoading, error } = useQuery({ queryKey: ["applications"], queryFn: api.applications });
  const { data: jobs } = useQuery({ queryKey: ["jobs"], queryFn: api.jobs });
  const filtered = useMemo(() => (data?.applications ?? []).filter((application) => {
    const matchesScore = scoreRangesSelected.some((selectedRange) => {
      const range = scoreRanges[selectedRange];
      return application.overallScore >= range.min && application.overallScore < range.max;
    });
    if (!matchesScore) return false;
    if (!jobsSelected.includes("all") && !jobsSelected.includes(application.jobId)) return false;
    if (!statusesSelected.includes("all") && !statusesSelected.includes(application.status)) return false;
    return `${application.candidateName} ${application.candidateEmail} ${application.candidatePhone} ${application.applicationId}`.toLowerCase().includes(search.toLowerCase());
  }), [data, jobsSelected.join(","), scoreRangesSelected.join(","), statusesSelected.join(","), search]);
  const updateMulti = (key: "job" | "score" | "status", values: string[]) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    next.delete("page");
    const defaults = key === "job" || key === "status" ? ["all"] : ["85-100"];
    if (key === "score" && values.includes("all")) {
      next.set(key, "all");
      setParams(next);
      return;
    }
    const selectedValues = values.filter((value) => value !== "all");
    if (selectedValues.length && !(key === "score" && selectedValues.length === 1 && selectedValues[0] === defaults[0])) selectedValues.forEach((value) => next.append(key, value));
    setParams(next);
  };
  const updateSearch = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("q", value); else next.delete("q");
    next.delete("page");
    setParams(next, { replace: true });
  };
  const updatePage = (value: number) => {
    const next = new URLSearchParams(params);
    if (value > 1) next.set("page", String(value)); else next.delete("page");
    setParams(next);
  };
  const updatePageSize = (value: number) => {
    const next = new URLSearchParams(params);
    if (value === pageSizeOptions[0]) next.delete("pageSize"); else next.set("pageSize", String(value));
    next.delete("page");
    setParams(next);
  };
  const updateSorts = (sorts: ApplicationSort[]) => {
    const next = new URLSearchParams(params);
    next.delete("sort");
    const isDefault = sorts.length === 1 && sorts[0]?.key === "score" && sorts[0].direction === "descending";
    if (!isDefault) sorts.forEach((sort) => next.append("sort", `${sort.key}:${sort.direction}`));
    next.delete("page");
    setParams(next);
  };
  const bulk = useMutation({ mutationFn: async () => {
    const chosen = data!.applications.filter((application) => selected.has(application.applicationId));
    const byJob = chosen.reduce<Record<string, string[]>>((groups, application) => {
      (groups[application.jobId] ??= []).push(application.applicationId);
      return groups;
    }, {});
    const grouped = Object.entries(byJob).map(([jobId, applicationIds]) => ({ jobId, applicationIds }));
    return api.bulkRescore(grouped);
  }, onSuccess: () => { setConfirmBulk(false); setToast(`${selected.size} candidates queued for background re-scoring`); setSelected(new Set()); } });
  if (isLoading) return <Loading label="Loading applications" />;
  if (error || !data) return <ErrorState error={error} />;
  return <><PageHeader eyebrow="Human review queue" title="Applications" description="Filter candidates by job, review status, and score range. Select multiple jobs, statuses, or score ranges when needed." actions={selected.size > 0 ? <button className="button primary" onClick={() => setConfirmBulk(true)}><RefreshCw size={16} />Re-score {selected.size}</button> : undefined} /><DependencyNotice areas={data.dependencies.sharePoint.affected} /><div className="toolbar filters-toolbar"><SearchField value={search} onChange={updateSearch} placeholder="Name, email, phone, or ID" /><MultiSelect label="Job" options={[{ value: "all", label: "All jobs" }, ...(jobs?.jobs.map((item) => ({ value: item.jobId, label: item.jobId })) ?? [])]} value={jobsSelected} onChange={(values) => updateMulti("job", values)} /><MultiSelect label="Status" options={[{ value: "all", label: "All statuses" }, ...APPLICATION_STATUSES.map((value) => ({ value, label: value }))]} value={statusesSelected} onChange={(values) => updateMulti("status", values)} className="applications-status-filter" /><MultiSelect label="Score range" options={[{ value: "all", label: scoreRanges.all.label }, ...Object.entries(scoreRanges).filter(([value]) => value !== "all").map(([value, range]) => ({ value, label: range.label }))]} value={scoreRangesSelected} onChange={(values) => updateMulti("score", values)} className="score-range-field" /><button className="button subtle filter-action" onClick={() => setParams({})}><RotateCcw size={15} />Reset</button></div><section className="panel"><ApplicationsTable applications={filtered} selected={selected} onToggle={(id) => setSelected((current) => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; })} {...(applicationSorts ? { sorts: applicationSorts } : {})} onSortsChange={updateSorts} pagination={{ page, pageSize, onPageChange: updatePage, onPageSizeChange: updatePageSize }} /></section><Dialog open={confirmBulk} title="Queue bulk re-scoring?" onClose={() => setConfirmBulk(false)} actions={<><button className="button subtle" onClick={() => setConfirmBulk(false)}>Cancel</button><button className="button primary" disabled={bulk.isPending} onClick={() => bulk.mutate()}>{bulk.isPending ? "Reserving…" : `Reserve ${selected.size} credits`}</button></>}><div className="credit-confirm"><Sparkles size={23} /><div><strong>{selected.size} candidates selected</strong><p>The full credit estimate is reserved before processing. Calls run in the background even if you close the browser; unused credits are released.</p></div></div></Dialog>{toast && <Toast message={toast} onClose={() => setToast(undefined)} />}</>;
}
